import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { Building2, Upload } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { useGroupCore } from './contexts/GroupIntakeRootContext';
import { AuthContext } from '../../contexts/AuthContext';
// We assume submitGroupIntake is for the final submission, not per-field persistence.
// import { submitGroupIntake } from './api-services';

const GroupDetailsStep = (props) => {
  const { auth } = useContext(AuthContext);
  const contextGroup = useGroupCore();

  const groupData = useMemo(() => {
    return props.group || contextGroup.group || {};
  }, [props.group, contextGroup.group]);

  const baseUpdateGroupField = props.updateGroup || contextGroup.updateGroupField;

  const [logoPreview, setLogoPreview] = useState(groupData.logoUrl || '');
  const hasAuthToken = !!auth?.token;

  const {
    register,
    // handleSubmit, // We might not use handleSubmit directly if auto-saving
    setValue,
    reset,
    formState: { errors: formErrors /*, isDirty, dirtyFields */ }, // isDirty and dirtyFields are not currently used
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      logoUrl: '',
      website: '',
      contact: {
        name: '',
        email: '',
        phone: '',
      },
    },
  });

  // Effect to initialize/reset form when external groupData changes
  useEffect(() => {
    const initialValues = {
      name: groupData.name || '',
      description: groupData.description || '',
      logoUrl: groupData.logoUrl || '',
      website: groupData.website || '',
      contact: {
        name: groupData.contact?.name || '',
        email: groupData.contact?.email || '',
        phone: groupData.contact?.phone || '',
      },
    };
    reset(initialValues);
    setLogoPreview(initialValues.logoUrl || ''); // Ensure logo preview is also set from these initial values
  }, [groupData, reset]);

  useEffect(() => {
    console.log('GroupDetailsStep - Auth token exists:', hasAuthToken);
    console.log('GroupDetailsStep - Group data for form:', groupData);
  }, [hasAuthToken, groupData]);

  const debouncedUpdateField = useDebouncedCallback((fieldName, value) => {
    if (!hasAuthToken) {
      console.warn('GroupDetailsStep: No auth token, skipping debounced update for', fieldName);
      return;
    }
    if (baseUpdateGroupField) {
      console.log(`GroupDetailsStep: Debounced update for ${fieldName}:`, value);
      baseUpdateGroupField(fieldName, value); 
      // This function (baseUpdateGroupField) is expected to handle API call or context update.
    } else {
      console.warn('GroupDetailsStep: updateGroupField function is not available.');
    }
  }, 1000); // 1-second debounce delay

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
    debouncedUpdateField(name, value);
  };

  const handleContactInputChange = (e) => {
    const { name: rawName, value } = e.target;
    // name will be like 'contact.name', 'contact.email'
    const fieldName = rawName; // RHF handles nested paths in register
    setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
    debouncedUpdateField(fieldName, value);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Result = reader.result;
        setLogoPreview(base64Result);
        setValue('logoUrl', base64Result, { shouldValidate: true, shouldDirty: true });
        debouncedUpdateField('logoUrl', base64Result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="form-content">
      <div className="section-title mb-4">
        <Building2 size={20} />
        Group Details
      </div>
      
      <Form.Group className="field-group">
        <Form.Label className="field-label">Group Name *</Form.Label>
        <Form.Control
          {...register('name', { required: 'Group Name is required' })}
          onChange={handleInputChange} // RHF's onChange is implicitly handled by register, but we add our own for debouncing
          placeholder="Enter group name"
          className="form-control"
          isInvalid={!!formErrors.name}
        />
        {formErrors.name && (
          <div className="field-error">{formErrors.name.message}</div>
        )}
      </Form.Group>
      
      <Form.Group className="field-group">
        <Form.Label className="field-label">Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          {...register('description')}
          onChange={handleInputChange}
          placeholder="Tell us about your anesthesia group"
          className="form-control"
          isInvalid={!!formErrors.description}
        />
        {formErrors.description && (
          <div className="field-error">{formErrors.description.message}</div>
        )}
      </Form.Group>

      <div className="section-title mt-4 mb-3">
        Contact Information
      </div>

      <Row>
        <Col md={4}>
          <Form.Group className="field-group">
            <Form.Label className="field-label">Contact Name</Form.Label>
            <Form.Control
              {...register('contact.name')}
              onChange={handleContactInputChange}
              placeholder="Contact person"
              className="form-control"
              isInvalid={!!formErrors.contact?.name}
            />
            {formErrors.contact?.name && (
              <div className="field-error">{formErrors.contact.name.message}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="field-group">
            <Form.Label className="field-label">Email</Form.Label>
            <Form.Control
              type="email"
              {...register('contact.email', {
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Entered value does not match email format',
                },
              })}
              onChange={handleContactInputChange}
              placeholder="Email address"
              className="form-control"
              isInvalid={!!formErrors.contact?.email}
            />
            {formErrors.contact?.email && (
              <div className="field-error">{formErrors.contact.email.message}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="field-group">
            <Form.Label className="field-label">Phone</Form.Label>
            <Form.Control
              type="tel"
              {...register('contact.phone')}
              onChange={handleContactInputChange}
              placeholder="Phone number"
              className="form-control"
              isInvalid={!!formErrors.contact?.phone}
            />
            {formErrors.contact?.phone && (
              <div className="field-error">{formErrors.contact.phone.message}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
      
      <Form.Group className="field-group mt-4">
        <Form.Label className="field-label">Group Logo</Form.Label>
        {/* Hidden input for RHF to track logoUrl if needed, though direct setValue is primary */}
        <input type="hidden" {...register('logoUrl')} /> 
        <div className="logo-upload-area">
          <div className="logo-preview">
            {logoPreview ? (
              <img src={logoPreview} alt="Group logo" className="img-fluid" />
            ) : (
              <div className="logo-placeholder">
                <Building2 size={40} />
                <span>No logo</span>
              </div>
            )}
          </div>
          <div className="logo-upload">
            <Form.Label htmlFor="logoUploadInput" className="custom-file-upload">
              <Upload size={16} className="me-2" />
              {logoPreview ? 'Change Logo' : 'Upload Logo'}
            </Form.Label>
            <Form.Control
              id="logoUploadInput"
              type="file"
              onChange={handleLogoChange}
              accept="image/*"
              className="d-none"
            />
            <p className="upload-help-text">
              Recommended size: 400x400px. File type: JPG, PNG, GIF.
            </p>
          </div>
        </div>
        {formErrors.logoUrl && (
          <div className="field-error">{formErrors.logoUrl.message}</div>
        )}
      </Form.Group>

      <Form.Group className="field-group">
        <Form.Label className="field-label">Website (if applicable)</Form.Label>
        <Form.Control
          type="url"
          {...register('website', {
            pattern: {
              value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/,
              message: 'Please enter a valid URL'
            }
          })}
          onChange={handleInputChange}
          placeholder="https://www.yourgroup.com"
          className="form-control"
          isInvalid={!!formErrors.website}
        />
        {formErrors.website && (
          <div className="field-error">{formErrors.website.message}</div>
        )}
      </Form.Group>
    </div>
  );
};

export default GroupDetailsStep;
